import { Brand, EBrand, User } from '@bonhams1793/bonhams-typescript'
import * as Sentry from '@sentry/nextjs'
import {
  QueryClient,
  QueryClientConfig,
  dehydrate,
} from '@tanstack/react-query'
import { NextPageContext } from 'next'
import type { AppContext, AppProps } from 'next/app'
import getConfig from 'next/config'
import Head from 'next/head'
import NextTopLoader from 'nextjs-toploader'

import { getCurrencyRates } from '@nx/api'
import { CURRENCY_RATES_KEY, CURRENCY_RATES_STALE_TIME } from '@nx/api/hooks'
import { createUserState } from '@nx/bidding-engine'
import { checkCookieBoxDismissed } from '@nx/check-cookie-box-dismissed'
import {
  LD_ID_COOKIE_NAME,
  LD_QUERY_KEY,
  getLaunchDarklyKey,
} from '@nx/external/launch-darkly-common'
import { ContextFeatureFlags, FeatureFlagsType } from '@nx/feature-flag'
import { FireGlobalStyles } from '@nx/fire/global-styles'
import { ToastSetup } from '@nx/fire/toast'
import { BonhamsCookies } from '@nx/global-types'
import {
  UtilsGoogleTagManager,
  getUserPersona,
} from '@nx/utils/google-tag-manager'
import { logger } from '@nx/utils/logger'

import { MetaData } from '@web/components/common/MetaData'
import { PageViewMonitor } from '@web/components/common/PageViewMonitor'
import { MainLayout } from '@web/components/layout'
import AppProviders from '@web/context/AppProviders'
import { CurrencyConfig } from '@web/context/UserInfoProvider'
import { launchDarklyFeatureFlags } from '@web/helpers/api/launchDarkly/flags'
import { getAllLaunchDarklyFlagsServer } from '@web/helpers/api/launchDarkly/server'
import { getUser, getUserInvoices } from '@web/helpers/api/user'
import { getUserBids } from '@web/helpers/api/user/getUserBids/getUserBids'
import { getUsersWonLots } from '@web/helpers/api/user/getUsersWonLots/getUsersWonLots'
import {
  CURRENCY_CONFIG_COOKIE,
  FeatureFlags,
  NOT_DEFINED_CURRENCY,
} from '@web/helpers/enums'
import { useSmoothScroll } from '@web/helpers/hooks'
import { redirectManager } from '@web/helpers/routing/redirect-manager'
import { getSiteBrand, getTheme, getUsersLanguage } from '@web/helpers/utils'
import { getIPAddress } from '@web/helpers/utils/getIPAddress/getIPAddress'
import { GlobalProps } from '@web/types/app'

const queryClientConfig: QueryClientConfig = {
  // Disable retrying failed queries (use axios interceptors to retry failed requests)
  defaultOptions: { queries: { retry: false } },
}

const { serverRuntimeConfig, publicRuntimeConfig } = getConfig()

function App({
  Component,
  pageProps,
  globalProps,
  cookies,
  currencyConfig,
}: CustomAppProps) {
  const brand = globalProps?.brand
  const headerFooterBrand =
    brand !== Brand.bonhams
      ? brand
      : pageProps?.headerFooterBrand || Brand.bonhams

  const theme = getTheme(brand)

  useSmoothScroll()

  return (
    <>
      <Head>
        <UtilsGoogleTagManager
          googleTagManagerId={publicRuntimeConfig.GTM_ID}
          userPersona={getUserPersona(cookies as BonhamsCookies)}
          brand={headerFooterBrand}
          selectedCurrency={
            currencyConfig?.userSelectedCurrency !== NOT_DEFINED_CURRENCY
              ? currencyConfig?.userSelectedCurrency
              : null
          }
        />
      </Head>
      <ContextFeatureFlags cookies={cookies || {}}>
        <AppProviders
          pageProps={pageProps}
          globalProps={globalProps}
          brand={brand}
          currencyConfig={currencyConfig}
          queryClientConfig={queryClientConfig}
        >
          <MetaData
            description="Bonhams Fine Art Auctioneers and Valuers: auctioneers of art, pictures, collectables and motor cars"
            baseUrl={publicRuntimeConfig.WEB_BASE_URL}
          />
          <NextTopLoader
            color={theme.colours.primary.primary}
            showForHashAnchor={false}
            showSpinner={false}
          />
          {pageProps?.removeMainLayout ? (
            <>
              <FireGlobalStyles />
              <Component {...pageProps} globalProps={globalProps} />
            </>
          ) : (
            <MainLayout
              globalProps={globalProps}
              headerFooterBrand={headerFooterBrand}
              currencyConfig={currencyConfig}
            >
              <PageViewMonitor brand={headerFooterBrand as EBrand} />
              <Component {...pageProps} globalProps={globalProps} />
            </MainLayout>
          )}

          <ToastSetup />
        </AppProviders>
      </ContextFeatureFlags>
    </>
  )
}

App.getInitialProps = async (
  appContext: AppContext
): Promise<AppInitialProps> => {
  const {
    ctx,
    ctx: { req, res },
  } = appContext
  const cookies = (req as NextPageContext['req'] & AppInitialProps)?.cookies

  const thirtyDaysInMilliseconds = 30 * 24 * 60 * 60 * 1000

  let launchDarklyKey = ''
  if (req && res) {
    const { default: Cookies } = await import('cookies')
    const cookieJar = new Cookies(req, res)

    const { key, isNew: isLaunchDarklyKeyNew } = getLaunchDarklyKey({
      withOneTrust: true,
      cookies: {
        OptanonAlertBoxClosed: cookieJar.get('OptanonAlertBoxClosed'),
        OptanonConsent: cookieJar.get('OptanonConsent'),
      },
      ldId: cookieJar.get(LD_ID_COOKIE_NAME),
    })
    launchDarklyKey = key
    if (isLaunchDarklyKeyNew) {
      cookieJar.set(LD_ID_COOKIE_NAME, launchDarklyKey, {
        path: '/',
        maxAge: thirtyDaysInMilliseconds,
        httpOnly: false,
      })
    }
  }

  const queryClient = new QueryClient(queryClientConfig)

  await queryClient.prefetchQuery({
    queryKey: LD_QUERY_KEY,
    queryFn: () =>
      getAllLaunchDarklyFlagsServer({
        ldId: cookies?.[LD_ID_COOKIE_NAME] || launchDarklyKey,
      }),
  })

  const launchDarklyKeys =
    queryClient.getQueryData<Record<`${launchDarklyFeatureFlags}`, boolean>>(
      LD_QUERY_KEY
    )

  const isPusherEnabled = launchDarklyKeys?.pusher || false

  /*
  Run redirect manager server side only for all routes even
  if they don't exist in app for purpose of redirecting old
  URLs to new e.g. /departments/*
  */
  if (
    req?.headers.host &&
    ctx.asPath &&
    res !== undefined &&
    !req.url?.includes('.json')
  ) {
    const redirect = redirectManager({
      hostname: req.headers.host,
      path: ctx.asPath,
      domains: [
        'www.bonhams.com',
        'skinner.bonhams.com',
        'csc.bonhams.com',
        'howToBuyRedirects',
      ],
    })

    if (redirect !== null) {
      res.setHeader('Location', redirect.destination)
      res.statusCode = redirect.permanent ? 308 : 307

      return {}
    }
  }

  await queryClient.prefetchQuery({
    queryKey: ['user'],
    queryFn: () => getUser({ ...cookies }),
  })

  const user: User | null | undefined = queryClient.getQueryData(['user'])

  await queryClient.prefetchQuery({
    queryKey: CURRENCY_RATES_KEY,
    queryFn: () => getCurrencyRates(publicRuntimeConfig.WEB_API_URL),
    staleTime: CURRENCY_RATES_STALE_TIME,
  })

  const userDismissCookieBox = checkCookieBoxDismissed({
    appContext,
  })

  const usersAcceptLanguage = getUsersLanguage(req?.headers['accept-language'])

  const userIP = getIPAddress(appContext.ctx.req)

  let currencyConfig
  try {
    currencyConfig = cookies?.[CURRENCY_CONFIG_COOKIE]
      ? (JSON.parse(cookies[CURRENCY_CONFIG_COOKIE]) as CurrencyConfig)
      : undefined
  } catch {
    currencyConfig = undefined
  }

  let userState = {}
  if (user && cookies?.bonhams_access) {
    try {
      const [activeBids, invoices, wonLots] = await Promise.all([
        getUserBids({ cookies, biddableLotsOnly: true, page_size: 300 }),
        getUserInvoices({
          paymentStatus: 'UNPAID',
          accessTokenSSR: cookies?.bonhams_access,
          refreshTokenSSR: cookies?.bonhams_token,
          userId: user.customerId,
        }),
        getUsersWonLots(cookies),
      ])

      userState = createUserState({
        activeBids: activeBids.bids,
        invoices: invoices.hits,
        wonLots,
      })
    } catch (e) {
      Sentry.captureException(e)
      logger.error(e)
    }
  }

  return {
    globalProps: {
      user: {
        ip: userIP,
        userDismissCookieBox,
        acceptLanguage: usersAcceptLanguage,
        launchDarklyKey,
      },
      dehydratedState: dehydrate(queryClient),
      brand: getSiteBrand(
        req?.headers.host,
        cookies?.[FeatureFlags.SITE_BRAND] as EBrand
      ),
      isPusherEnabled,
      ssrAppStoreState: userState,
    },
    cookies,
    currencyConfig,
    baseUrl: serverRuntimeConfig.WEB_BASE_URL,
  }
}

export default App

export interface AppInitialProps {
  globalProps?: GlobalProps
  cookies?: FeatureFlagsType
  currencyConfig?: CurrencyConfig
  baseUrl?: string
}

export type CustomAppProps = AppProps & AppInitialProps
